import React, { useState, useEffect } from "react";
import {
  getFirestore,
  doc,
  updateDoc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  serverTimestamp
} from "firebase/firestore";
import { app } from "../../firebaseConfig";

const FormulaireTypeA = ({ productId, orderId, onComplete, produit, index }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Debug avec useEffect pour éviter la boucle infinie
  useEffect(() => {
    console.log("🔍 FormulaireTypeA monté avec:", { 
      productId, 
      orderId, 
      produitName: produit?.name,
      index 
    });
  }, [productId, orderId, produit?.name, index]);
  
  const [formData, setFormData] = useState({
    verificateur: { Nom: "", Adresse: "", Contact: "", DateVerification: "" },
    client: { Nom: "", Adresse: "", Contact: "" },
    vehicule: {
      Type: "",
      NumeroImmatriculation: "",
      MarqueCaisse: "",
      NumeroIdentificationCaisse: "",
    },
    porte: { NumeroSerie: "" },
    controles: {
      tablier: {},
      rails: {},
      railsDetailles: {
        coteRailHorizontalChauffeur: "",
        coteRailHorizontalPassager: "",
      },
      ressorts: {},
      cadreArriere: {},
      cadreArriereDetaille: {
        parodiesLateralesBois: "",
        parodiesLateralesIsolees: "",
        pavillonTranslucide: "",
        pavillonIsole: ""
      },
      relevesMesures: { 
        pression: "", 
        montee: "", 
        descente: "", 
        batterie: "", 
        fuite: "" 
      },
    },
    declarationHonneur: false
  });

  const db = getFirestore(app);

  const handleChange = (section, field, value) => {
    try {
      setFormData((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
      // Clear error when user starts typing
      if (errors[`${section}.${field}`]) {
        setErrors(prev => ({ ...prev, [`${section}.${field}`]: null }));
      }
    } catch (error) {
      console.error("❌ Erreur dans handleChange:", error);
    }
  };

  const handleControleChange = (controle, field, value) => {
    try {
      setFormData((prev) => ({
        ...prev,
        controles: {
          ...prev.controles,
          [controle]: { ...prev.controles[controle], [field]: value },
        },
      }));
    } catch (error) {
      console.error("❌ Erreur dans handleControleChange:", error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validation des champs obligatoires
    const requiredFields = [
      { section: 'verificateur', field: 'Nom', label: 'Nom du vérificateur' },
      { section: 'verificateur', field: 'DateVerification', label: 'Date de vérification' },
      { section: 'client', field: 'Nom', label: 'Nom du client' },
      { section: 'vehicule', field: 'Type', label: 'Type de véhicule' },
      { section: 'porte', field: 'NumeroSerie', label: 'Numéro de série' },
    ];

    requiredFields.forEach(({ section, field, label }) => {
      if (!formData[section][field]) {
        newErrors[`${section}.${field}`] = `${label} est obligatoire`;
      }
    });

    if (!formData.declarationHonneur) {
      newErrors.declarationHonneur = "Déclaration sur l'honneur requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔧 FONCTION HELPER: Nettoyer les valeurs undefined pour Firebase
  const cleanObjectForFirebase = (obj) => {
    if (obj === null || obj === undefined) return null;
    
    if (Array.isArray(obj)) {
      return obj.map(item => cleanObjectForFirebase(item)).filter(item => item !== null);
    }
    
    if (typeof obj === 'object') {
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = cleanObjectForFirebase(value);
        if (cleanedValue !== null && cleanedValue !== undefined) {
          cleaned[key] = cleanedValue;
        }
      }
      return Object.keys(cleaned).length > 0 ? cleaned : null;
    }
    
    return obj;
  };

  // 🔥 FONCTION handleSubmit CORRIGÉE
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert("⚠️ Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);

    try {
      if (!orderId) {
        throw new Error("OrderId manquant - impossible de sauvegarder");
      }

      // ✅ S'assurer qu'aucune valeur n'est undefined
      const safeProductId = productId || `product_${index || 0}_${Date.now()}`;
      const safeIndex = index !== undefined ? index : 0;
      const safeProduitName = produit?.name || "Produit Standard";

      console.log("🔍 Sauvegarde avec:", { 
        orderId, 
        safeProductId, 
        safeIndex, 
        originalProductId: productId,
        safeProduitName 
      });

      const db = getFirestore(app);
      
      // 1. Mise à jour du produit dans le dossier
      const dossierRef = doc(db, "dossiers", orderId);
      const dossierSnap = await getDoc(dossierRef);
      
      if (!dossierSnap.exists()) {
        throw new Error("Dossier non trouvé");
      }

      const dossierData = dossierSnap.data();
      const updatedProducts = [...(dossierData.produits || [])];
      
      // Marquer ce produit comme rempli
      if (updatedProducts[safeIndex]) {
        updatedProducts[safeIndex] = {
          ...updatedProducts[safeIndex],
          id: updatedProducts[safeIndex].id || safeProductId,
          filled: true,
          formulaireData: formData,
          formulaireType: "TypeA_Standard",
          updatedAt: new Date().toISOString()
        };
      } else {
        updatedProducts[safeIndex] = {
          id: safeProductId,
          name: safeProduitName,
          filled: true,
          formulaireData: formData,
          formulaireType: "TypeA_Standard",
          createdAt: new Date().toISOString()
        };
      }

      // Générer l'ID de déclaration
      let declarationId;
      if (productId && productId !== 'default' && productId !== null) {
        declarationId = `${productId}_unit_1`;
      } else {
        const baseName = safeProduitName.toLowerCase().replace(/[^a-z0-9]/g, '');
        declarationId = `${baseName}_${safeIndex}_unit_1`;
      }
      
      console.log("🔑 ID de déclaration généré:", declarationId);
      console.log("🔑 ProductId original:", productId);
      
      // Préparer les données de base
      const baseUpdateData = {
        produits: updatedProducts,
        lastModified: new Date().toISOString(),
        
        // Flags principaux avec declarationId
        [`${declarationId}_filled`]: true,
        [`${declarationId}_montage_complete`]: true,
        [`${declarationId}_status`]: "controle_montage_complete",
        
        // Flag global pour ce produit
        [`produit_${safeIndex}_complete`]: true,
      };

      // Ajouter les flags productId seulement si productId existe
      if (productId && productId !== 'default' && productId !== null && productId !== undefined) {
        baseUpdateData[`${productId}_filled`] = true;
        baseUpdateData[`${productId}_montage_complete`] = true;
        baseUpdateData[`${productId}_unit_1_filled`] = true;
        baseUpdateData[`${productId}_unit_1_montage_complete`] = true;
        baseUpdateData[`${productId}_status`] = "controle_montage_complete";
      }

      // Structure de données pour le formulaire
      const formulaireData = {
        type: "TypeA_Standard",
        produitName: safeProduitName,
        formData: formData,
        completedAt: new Date().toISOString(),
        productIndex: safeIndex,
        declarationId: declarationId,
      };

      // N'ajouter productId que s'il existe vraiment
      if (productId && productId !== 'default' && productId !== null) {
        formulaireData.productId = productId;
      }

      baseUpdateData[`formulaire_${safeProductId}`] = formulaireData;

      // ✅ Nettoyer tout l'objet avant l'envoi à Firebase
      const updateData = cleanObjectForFirebase(baseUpdateData);

      console.log("💾 Données de mise à jour nettoyées:", updateData);
      console.log("🔥 FLAGS CRÉÉS POUR LE DASHBOARD:");
      Object.keys(updateData).filter(key => 
        key.includes('filled') || key.includes('complete') || key.includes('status')
      ).forEach(key => {
        console.log(`  - ${key}: ${updateData[key]}`);
      });
      
      await updateDoc(dossierRef, updateData);
      console.log("✅ Dossier mis à jour avec succès");

      // 2. Vérification post-sauvegarde
      try {
        const verificationSnap = await getDoc(dossierRef);
        const verificationData = verificationSnap.data();
        
        console.log("🔍 VÉRIFICATION POST-SAUVEGARDE:");
        console.log(`  - ${declarationId}_filled: ${verificationData[`${declarationId}_filled`]}`);
        console.log(`  - ${declarationId}_montage_complete: ${verificationData[`${declarationId}_montage_complete`]}`);
        
        if (productId && productId !== 'default' && productId !== null) {
          console.log(`  - ${productId}_filled: ${verificationData[`${productId}_filled`]}`);
          console.log(`  - ${productId}_montage_complete: ${verificationData[`${productId}_montage_complete`]}`);
        }
        
      } catch (verificationError) {
        console.warn("⚠️ Erreur vérification:", verificationError);
      }

      // 3. Sauvegarde dans la collection formulaires
      try {
        const formulaireDocId = `${orderId}_${safeProductId}`;
        const formulaireRef = doc(db, "formulaires", formulaireDocId);
        
        const formulaireCompleteData = cleanObjectForFirebase({
          orderId: orderId,
          productId: safeProductId,
          productIndex: safeIndex,
          type: "TypeA_Standard",
          produitName: safeProduitName,
          formData: formData,
          createdAt: new Date().toISOString(),
          declarationId: declarationId,
          createdBy: "carrossier",
          originalProductId: productId && productId !== 'default' ? productId : null
        });
        
        await setDoc(formulaireRef, formulaireCompleteData);
        console.log("✅ Formulaire sauvegardé");
      } catch (formulaireError) {
        console.warn("⚠️ Erreur formulaire:", formulaireError);
      }

      alert(`✅ Contrôle montage et mise en service sauvegardé avec succès !

🔑 ID déclaration: ${declarationId}
📊 ProductId: ${productId || 'généré automatiquement'}
🆔 Safe ProductId: ${safeProductId}

Le statut devrait maintenant apparaître dans le dashboard carrossier.`);
      
      if (onComplete) onComplete();

    } catch (error) {
      console.error("❌ Erreur lors de la sauvegarde:", error);
      
      let errorMessage = "Erreur lors de la sauvegarde: ";
      
      if (error.code === 'invalid-argument') {
        errorMessage += "Données invalides. Vérifiez que tous les champs sont correctement remplis.";
      } else if (error.code === 'permission-denied') {
        errorMessage += "Permissions insuffisantes.";
      } else {
        errorMessage += error.message;
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const renderRadioGroup = (controle, field, label) => {
    try {
      return (
        <div className="grid grid-cols-4 items-center gap-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors" key={`${controle}-${field}`}>
          <label className="text-sm font-medium text-gray-900">{label}</label>
          {["O", "So", "N"].map((opt) => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`${controle}_${field}_${productId || 'default'}`}
                value={opt}
                checked={formData.controles[controle]?.[field] === opt}
                onChange={() => handleControleChange(controle, field, opt)}
                className="text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-700">{opt}</span>
            </label>
          ))}
        </div>
      );
    } catch (error) {
      console.error("❌ Erreur dans renderRadioGroup:", error);
      return <div className="text-red-500">Erreur d'affichage pour: {label}</div>;
    }
  };

  const renderInputField = (section, field, placeholder, type = "text", required = false) => {
    const errorKey = `${section}.${field}`;
    try {
      return (
        <div>
          <input
            type={type}
            placeholder={placeholder}
            value={formData[section]?.[field] || ""}
            onChange={(e) => handleChange(section, field, e.target.value)}
            className={`w-full px-4 py-3 border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
              errors[errorKey] ? 'border-red-500' : 'border-gray-200'
            }`}
            required={required}
          />
          {errors[errorKey] && (
            <p className="text-red-500 text-xs mt-1">{errors[errorKey]}</p>
          )}
        </div>
      );
    } catch (error) {
      console.error("❌ Erreur dans renderInputField:", error);
      return <div className="text-red-500">Erreur d'affichage pour: {placeholder}</div>;
    }
  };

  try {
    return (
      <div className="max-w-6xl mx-auto space-y-8">
        {/* En-tête du formulaire Type A */}
        <div className="bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 p-6 rounded-2xl border border-orange-200">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-600 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-xl">🚪</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Contrôle montage et mise en service
              </h2>
              <p className="text-gray-600">Configuration Standard - {produit?.name || "Produit Standard"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1.5 bg-orange-100 text-orange-800 rounded-full text-sm font-medium border border-orange-200">
              Configuration Standard - Roulettes FIT+ (Rouge) + Supports renforcés
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Section 1 : Identification du vérificateur */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">👤</span>
              1. Identification du vérificateur
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {renderInputField("verificateur", "Nom", "Nom du vérificateur *", "text", true)}
              {renderInputField("verificateur", "Adresse", "Adresse complète", "text")}
              {renderInputField("verificateur", "Contact", "Téléphone / Email", "text")}
              {renderInputField("verificateur", "DateVerification", "", "date", true)}
            </div>
          </section>

          {/* Section 2 : Client final */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">🏢</span>
              2. Identification client final ou livré
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {renderInputField("client", "Nom", "Nom / Raison sociale *", "text", true)}
              {renderInputField("client", "Adresse", "Adresse de livraison", "text")}
              {renderInputField("client", "Contact", "Contact client", "text")}
            </div>
          </section>

          {/* Section 3 : Véhicule */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">🚛</span>
              3. Informations véhicule
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <select
                  value={formData.vehicule?.Type || ""}
                  onChange={(e) => handleChange("vehicule", "Type", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                    errors["vehicule.Type"] ? 'border-red-500' : 'border-gray-200'
                  }`}
                  required
                >
                  <option value="">Sélectionner le type de véhicule *</option>
                  <option value="Porteur">Porteur</option>
                  <option value="Semi">Semi-remorque</option>
                  <option value="Autre">Autre</option>
                </select>
                {errors["vehicule.Type"] && (
                  <p className="text-red-500 text-xs mt-1">{errors["vehicule.Type"]}</p>
                )}
              </div>
              {renderInputField("vehicule", "NumeroImmatriculation", "N° d'immatriculation", "text")}
              {renderInputField("vehicule", "MarqueCaisse", "Marque de la caisse", "text")}
              {renderInputField("vehicule", "NumeroIdentificationCaisse", "N° d'identification de la caisse", "text")}
            </div>
          </section>

          {/* Section 4 : Porte */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">🚪</span>
              4. Informations porte
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {renderInputField("porte", "NumeroSerie", "N° de série de la porte *", "text", true)}
              <div className="flex items-center">
                <div className="text-sm text-gray-600 bg-gray-50 px-4 py-3 rounded-xl border">
                  <span className="font-medium">Type de produit :</span> {produit?.name || "Produit Standard"}
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 : Contrôles tablier TYPE A (Standard) */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">🔧</span>
              5. Contrôles tablier - Configuration Standard
            </h3>
            
            <div className="bg-orange-50 p-4 rounded-xl mb-6 border border-orange-200">
              <div className="grid grid-cols-4 gap-4 font-semibold text-sm text-orange-800">
                <span>Élément à contrôler</span>
                <span className="text-center">O (Oui)</span>
                <span className="text-center">So (Sans objet)</span>
                <span className="text-center">N (Non)</span>
              </div>
            </div>
            
            <div className="space-y-1">
              {[
                ["A", "Calage des roulettes"],
                ["B", "Roulettes FIT+ (Rouge)"],
                ["C", "Bras haut roulettes FIT X-Trem (Bleu)"],
                ["D", "Roulettes Fit X-Trem (bleu)"],
                ["E", "Ressorts de calage pour roulettes Fit X-trem (bleu)"],
                ["F", "Supports de roulettes renforcées"],
                ["G", "Serrage des vis des écrous"],
                ["H", "Axes des charnières"],
                ["I", "Joints latéraux"],
                ["J", "Finition apprêtée"],
                ["K", "Finition millenium (CP Blanc) ou laqué blanc pour Forty et RR"],
                ["L", "Finition peinture"],
                ["M", "Plaque de firme avec QR-Code instruction (face extérieure de la porte)"],
                ["N", "Sticker sécurité section basse"],
                ["O", "Plaque CE sur section haute côté intérieur"],
              ].map(([code, label]) => 
                renderRadioGroup("tablier", code, `${code} - ${label}`)
              )}
            </div>
          </section>

          {/* Section 6 : Contrôles rails TYPE A */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">🛤️</span>
              6. Contrôles rails
            </h3>
            
            <div className="space-y-1 mb-6">
              {[
                ["A", "Jonction rails horizontaux/verticaux (continue)"],
                ["B", "Fixation rails verticaux"],
                ["C", "Fixation rails horizontaux"],
                ["F", "Parallélisme rails verticaux/horizontaux"],
                ["G", "Jeu constant porte/rails"]
              ].map(([code, label]) => 
                renderRadioGroup("rails", code, `${code} - ${label}`)
              )}
            </div>

            {/* Côtes et mesures pour Type A */}
            <div className="bg-blue-50 p-4 rounded-xl">
              <h4 className="font-semibold text-gray-900 mb-4">Côtes entre le rail horizontal et le pavillon</h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">D - Côté chauffeur - Relevé en mm</label>
                  <input
                    type="number"
                    placeholder="Mesure en mm"
                    value={formData.controles.railsDetailles?.coteRailHorizontalChauffeur || ""}
                    onChange={(e) => handleControleChange("railsDetailles", "coteRailHorizontalChauffeur", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E - Côté passager - Relevé en mm</label>
                  <input
                    type="number"
                    placeholder="Mesure en mm"
                    value={formData.controles.railsDetailles?.coteRailHorizontalPassager || ""}
                    onChange={(e) => handleControleChange("railsDetailles", "coteRailHorizontalPassager", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section 7 : Ressorts TYPE A */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">⚙️</span>
              7. Ressorts
            </h3>
            
            <div className="space-y-1">
              {[
                ["A", "Attaches câbles"],
                ["B", "Goupilles attaches câbles"],
                ["C", "Goupilles arbre ressort"],
                ["D", "Serrage pièces d'immobilisation"],
                ["E", "Serrage support central"],
                ["F", "Position des câbles"],
                ["G", "Tension"]
              ].map(([code, label]) => 
                renderRadioGroup("ressorts", code, `${code} - ${label}`)
              )}
            </div>
          </section>

          {/* Section 8 : Caisse / Cadre arrière TYPE A */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">🏗️</span>
              8. Caisse / Cadre arrière
            </h3>
            
            <div className="space-y-6">
              {/* Contrôles de base */}
              <div className="space-y-1">
                {[
                  ["A", "Équerrage cadre arrière"],
                  ["B", "Parallélisme"],
                  ["C", "Parodies latérales"]
                ].map(([code, label]) => 
                  renderRadioGroup("cadreArriere", code, `${code} - ${label}`)
                )}
              </div>

              {/* Parodies latérales détaillées */}
              <div className="bg-purple-50 p-4 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-4">Détails parodies latérales</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.controles.cadreArriereDetaille?.parodiesLateralesBois === "true"}
                        onChange={(e) => handleControleChange("cadreArriereDetaille", "parodiesLateralesBois", e.target.checked ? "true" : "")}
                        className="text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Isolées</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Type de pavillon */}
              <div className="bg-indigo-50 p-4 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-4">D - Type de pavillon</h4>
                <div className="grid grid-cols-4 items-center gap-4 mb-4">
                  <span className="text-sm font-medium text-gray-900">Type de pavillon</span>
                  {["O", "So", "N"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={`pavillon_${productId || 'default'}`}
                        value={opt}
                        checked={formData.controles.cadreArriere?.typePavillon === opt}
                        onChange={() => handleControleChange("cadreArriere", "typePavillon", opt)}
                        className="text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.controles.cadreArriereDetaille?.pavillonTranslucide === "true"}
                        onChange={(e) => handleControleChange("cadreArriereDetaille", "pavillonTranslucide", e.target.checked ? "true" : "")}
                        className="text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Translucide</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.controles.cadreArriereDetaille?.pavillonIsole === "true"}
                        onChange={(e) => handleControleChange("cadreArriereDetaille", "pavillonIsole", e.target.checked ? "true" : "")}
                        className="text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Isolé</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 9 : Relevés et mesures TYPE A */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">📊</span>
              9. Relevés et mesures
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pression (si applicable)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Ex: 12.5"
                  value={formData.controles.relevesMesures?.pression || ""}
                  onChange={(e) => handleControleChange("relevesMesures", "pression", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Temps montée (s)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Ex: 25.5"
                  value={formData.controles.relevesMesures?.montee || ""}
                  onChange={(e) => handleControleChange("relevesMesures", "montee", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Temps descente (s)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Ex: 30.2"
                  value={formData.controles.relevesMesures?.descente || ""}
                  onChange={(e) => handleControleChange("relevesMesures", "descente", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tension batterie (V)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Ex: 12.6"
                  value={formData.controles.relevesMesures?.batterie || ""}
                  onChange={(e) => handleControleChange("relevesMesures", "batterie", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Contrôle des fuites (simplifié pour Type A) */}
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
              <div className="mb-3">
                <span className="font-medium text-gray-900">Fuites détectées</span>
                <p className="text-sm text-gray-600 mt-1">
                  (contrôle visuel et détection)
                </p>
              </div>
              
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`fuites_${productId || 'default'}`}
                    value="oui"
                    checked={formData.controles.relevesMesures?.fuite === "oui"}
                    onChange={() => handleControleChange("relevesMesures", "fuite", "oui")}
                    className="text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Oui</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`fuites_${productId || 'default'}`}
                    value="non"
                    checked={formData.controles.relevesMesures?.fuite === "non"}
                    onChange={() => handleControleChange("relevesMesures", "fuite", "non")}
                    className="text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Non</span>
                </label>
              </div>
            </div>
          </section>

          {/* Section 10 : Déclaration sur l'honneur */}
          <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-6 text-orange-600 flex items-center gap-2">
              <span className="text-xl">✅</span>
              10. Déclaration sur l'honneur
            </h3>
            
            <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
              <p className="text-sm text-gray-700 mb-4">
                Je soussigné(e), certifie que le montage de la porte automatique <strong>{produit?.name || "Produit Standard"}</strong> a été effectué conformément aux règles de l'art et aux prescriptions techniques du fabricant. Tous les contrôles de sécurité ont été réalisés avec succès.
              </p>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.declarationHonneur}
                  onChange={(e) => setFormData(prev => ({ ...prev, declarationHonneur: e.target.checked }))}
                  className={`w-4 h-4 text-orange-600 focus:ring-orange-500 ${errors.declarationHonneur ? 'border-red-500' : ''}`}
                />
                <div>
                  <span className="font-medium text-gray-900">J'atteste sur l'honneur l'exactitude de ces informations *</span>
                  <p className="text-sm text-gray-600 mt-1">
                    Cette déclaration engage ma responsabilité professionnelle
                  </p>
                </div>
              </label>
              {errors.declarationHonneur && (
                <p className="text-red-500 text-xs mt-2">{errors.declarationHonneur}</p>
              )}
            </div>
          </section>

          {/* Validation et soumission */}
          <section className="bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 p-6 rounded-2xl border border-orange-200">
            <div className="flex items-start gap-4">
              <div className="text-orange-600 text-2xl mt-1">⚠️</div>
              <div>
                <h4 className="font-semibold text-orange-800 mb-2">Validation du contrôle - Configuration Standard</h4>
                <p className="text-orange-700 text-sm mb-4">
                  En soumettant ce formulaire, je certifie avoir effectué tous les contrôles requis 
                  selon les procédures techniques standard et que l'installation est conforme aux spécifications.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform ${
                      loading
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enregistrement en cours...
                      </>
                    ) : (
                      <>
                        <span className="text-xl">✅</span>
                        Enregistrer formulaire Standard
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="flex items-center gap-2 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                  >
                    <span>←</span>
                    Retour
                  </button>
                </div>
              </div>
            </div>
          </section>
        </form>
      </div>
    );
  } catch (error) {
    console.error("❌ Erreur de rendu dans FormulaireTypeA:", error);
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">❌ Erreur d'affichage du formulaire</h3>
          <p className="text-red-700 mb-4">
            Une erreur s'est produite lors du chargement du formulaire TypeA.
          </p>
          <div className="bg-red-100 p-3 rounded text-sm text-red-800">
            <strong>Détails de l'erreur :</strong>
            <pre>{error.message}</pre>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            🔄 Recharger la page
          </button>
        </div>
      </div>
    );
  }
};

export default FormulaireTypeA